const TOKEN_KEY = "agent_wiki_token";

/**
 * Retrieve the stored JWT from localStorage.
 * Returns null when no token is present.
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Persist a JWT in localStorage.
 */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Remove the stored JWT (sign out).
 */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Returns true when a token is present.
 * Does NOT validate the token signature or expiry — that is the server's job.
 */
export function isAuthenticated(): boolean {
  return getToken() !== null;
}

/**
 * Decode the payload of a JWT without verification.
 * Useful for reading claims (e.g. sub, email) on the client side.
 */
export function decodeTokenPayload<T = Record<string, unknown>>(
  token: string
): T | null {
  try {
    const [, payloadB64] = token.split(".");
    const json = atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

/**
 * Return the decoded payload of the currently stored token, or null.
 */
export function getCurrentUser<T = Record<string, unknown>>(): T | null {
  const token = getToken();
  if (!token) return null;
  return decodeTokenPayload<T>(token);
}
